import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import useAuth from '../hooks/useAuth';
import SortFilter, { sortItems } from '../components/SortFilter';

export default function AttendancePage() {
  const navigate = useNavigate();
  const { currentBranch, user } = useAuth();

  const [attendance, setAttendance] = useState([]);
  const [branches, setBranches] = useState([]);
  const [classesList, setClassesList] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [sortOption, setSortOption] = useState('a-z');
  const [filters, setFilters] = useState({ 
    branch_id: currentBranch ? currentBranch.branch_id : '', 
    class_id: '',
    date: new Date().toISOString().split('T')[0] 
  });
  const [summary, setSummary] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    loadBranches();
  }, []);

  useEffect(() => {
    if (currentBranch) {
      setFilters(p => ({ ...p, branch_id: currentBranch.branch_id }));
    }
  }, [currentBranch]);

  useEffect(() => {
    const targetBranchId = filters.branch_id || currentBranch?.branch_id;
    if (targetBranchId) {
      loadClasses(targetBranchId);
    } else {
      setClassesList([]);
    }
  }, [filters.branch_id, currentBranch]);

  async function loadClasses(branchId) {
    setClassesLoading(true);
    try {
      const res = await api.get(`/branches/${branchId}/classes`);
      const list = (res.data.data || res.data.classes || []).filter(c => c.is_active === 1 || c.is_active === undefined);
      setClassesList(list);
    } catch (err) {
      setClassesList([]);
    } finally {
      setClassesLoading(false);
    }
  }

  useEffect(() => {
    loadAttendance();
  }, [filters.branch_id, filters.class_id, filters.date]);

  async function loadBranches() {
    try {
      const response = await api.get('/admin/branches');
      setBranches(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load branches');
    }
  }

  async function loadAttendance() {
    try {
      const params = { date: filters.date };
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (filters.class_id) params.class_id = filters.class_id;
      const response = await api.get('/admin/attendance', { params });
      setAttendance(response.data.data || []);
      setSummary(response.data.summary || {});
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load attendance');
    }
  }

  const markStatus = async (record, status) => {
    setError('');
    const targetBranchId = record.branch_id || currentBranch?.branch_id || filters.branch_id;
    if (!targetBranchId) {
      setError('Branch ID is required to update attendance');
      return;
    }
    try {
      await api.post('/admin/attendance', {
        student_id: record.student_id,
        branch_id: targetBranchId,
        date: filters.date,
        status,
        note: record.note || undefined,
      });
      loadAttendance();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update attendance');
    }
  };

  const sortedAttendance = sortItems(attendance, sortOption, 'full_name', 'created_at');

  return (
    <div>
      <PageHeader title="Attendance Management" subtitle="View daily attendance, history, and corrections" />
      {error && <div className="error-message">{error}</div>}

      <div className="filter-row" style={{ flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        {user?.role !== 'center_manager' ? (
          <select value={filters.branch_id} onChange={(e) => setFilters((p) => ({ ...p, branch_id: e.target.value, class_id: '' }))}>
            <option value="">All branches</option>
            {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
          </select>
        ) : (
          <div style={{
            padding: '8px 16px',
            background: '#f3f4f6',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151'
          }}>
            📍 Branch: {currentBranch?.name}
          </div>
        )}

        {(filters.branch_id || user?.role === 'center_manager' || currentBranch) && (
          <select
            value={filters.class_id}
            onChange={(e) => setFilters((p) => ({ ...p, class_id: e.target.value }))}
            disabled={classesLoading}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              background: '#fff',
              fontSize: 14,
              fontWeight: 500,
              color: '#334155',
              cursor: 'pointer',
              outline: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              boxSizing: 'border-box'
            }}
          >
            <option value="">All Classes</option>
            {classesList.map((cls) => (
              <option key={cls.id} value={cls.id}>
                {cls.name}{cls.section ? ` (${cls.section})` : ''}
              </option>
            ))}
          </select>
        )}

        <input type="date" value={filters.date} onChange={(e) => setFilters((p) => ({ ...p, date: e.target.value }))} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, cursor: 'pointer', background: '#ffffff' }} />

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <SortFilter sortOption={sortOption} onSortChange={setSortOption} />
        </div>
      </div>

      <div className="dashboard-grid compact-grid">
        {['present', 'absent', 'late', 'unmarked'].map((key) => (
          <div className="summary-card" key={key}>
            <p>{key}</p>
            <strong>{summary[key] || 0}</strong>
          </div>
        ))}
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Student</th>
              <th>Branch</th>
              <th>Status</th>
              <th>Note</th>
              <th>Correction</th>
            </tr>
          </thead>
          <tbody>
            {sortedAttendance.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                  No attendance records found for the selected date/branch.
                </td>
              </tr>
            ) : (
              sortedAttendance.map((record) => (
                <tr key={record.student_id}>
                  <td
                    onClick={() => record.student_id && navigate(`/students/${record.student_id}?from=attendance`)}
                    style={{ cursor: 'pointer', color: '#2563eb', fontWeight: 500 }}
                  >
                    {record.full_name}
                  </td>
                  <td>{record.branch_name || '-'}</td>
                  <td>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      background: record.status === 'present' ? '#d1fae5' : record.status === 'late' ? '#fef3c7' : record.status === 'absent' ? '#fee2e2' : '#f3f4f6',
                      color: record.status === 'present' ? '#065f46' : record.status === 'late' ? '#92400e' : record.status === 'absent' ? '#991b1b' : '#374151'
                    }}>
                      {record.status}
                    </span>
                  </td>
                  <td>{record.note || '-'}</td>
                  <td>
                    <select
                      value={record.status || 'unmarked'}
                      onChange={(e) => markStatus(record, e.target.value)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        cursor: 'pointer',
                        background: '#fff'
                      }}
                    >
                      <option value="unmarked">unmarked</option>
                      <option value="present">present</option>
                      <option value="absent">absent</option>
                      <option value="late">late</option>
                    </select>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
